import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';

const FilterBar = ({
  teams,
  loadingTeams,
  selectedTeam,
  onTeamSelect,
  games,
  loadingGames,
  selectedGame,
  onGameSelect,
  isAggregate,
  onAggregateToggle
}) => {
  const [teamSearch, setTeamSearch] = useState('');
  const [teamOpen, setTeamOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const teamRef = useRef(null);
  const matchRef = useRef(null);

  useEffect(() => {
    function onOutsideClick(e) {
      if (teamRef.current && !teamRef.current.contains(e.target)) setTeamOpen(false);
      if (matchRef.current && !matchRef.current.contains(e.target)) setMatchOpen(false);
    }
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, []);

  const filteredTeams = teams.filter((t) =>
    t.name.toLowerCase().includes(teamSearch.toLowerCase())
  );

  function handleTeamSelect(team) {
    onTeamSelect(team);
    setTeamOpen(false);
    setTeamSearch('');
  }

  function handleGameSelect(game) {
    onGameSelect(game);
    setMatchOpen(false);
  }

  const matchLabel = selectedGame
    ? `${selectedGame.home?.name} vs ${selectedGame.away?.name}`
    : 'Match';
  const isSelectedGameLive = selectedGame?.status === 'in-play';

  return (
    <div className="flex items-center gap-3 px-6 py-4 bg-white border-b border-[#E2E8F0]">
      {/* Team dropdown */}
      <div ref={teamRef} className="relative">
        <button
          onClick={() => setTeamOpen((o) => !o)}
          className={`h-9 px-4 rounded-lg flex items-center gap-2 text-[13px] font-medium border transition-colors ${
            selectedTeam
              ? 'bg-[#2D47B8] text-white border-[#2D47B8]'
              : 'bg-white text-[#334155] border-[#CBD5E1] hover:border-[#94A3B8]'
          }`}
        >
          {selectedTeam ? selectedTeam.name : 'Team'}
          <svg
            className={`w-3 h-3 transition-transform ${teamOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {teamOpen && (
          <div className="absolute top-[calc(100%+6px)] left-0 w-[240px] bg-white border border-[#CBD5E1] rounded-xl shadow-lg z-50">
            <div className="p-2 border-b border-[#E2E8F0]">
              <input
                autoFocus
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                placeholder="Search teams…"
                className="w-full h-8 px-3 rounded-lg border border-[#CBD5E1] text-[13px] outline-none focus:border-[#2D47B8]"
              />
            </div>
            <div className="max-h-[240px] overflow-y-auto py-1">
              {loadingTeams ? (
                <div className="px-3 py-3 text-[13px] text-[#94A3B8]">Loading…</div>
              ) : filteredTeams.length === 0 ? (
                <div className="px-3 py-3 text-[13px] text-[#94A3B8]">No teams found</div>
              ) : (
                filteredTeams.map((t) => (
                  <button
                    key={t._id}
                    onClick={() => handleTeamSelect(t)}
                    className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#F1F5F9] transition-colors ${
                      selectedTeam?._id === t._id
                        ? 'bg-[#EEF2FF] text-[#3B5BDB] font-medium'
                        : 'text-[#334155]'
                    }`}
                  >
                    {t.name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Match dropdown */}
      <div ref={matchRef} className="relative">
        <button
          disabled={!selectedTeam || isAggregate}
          onClick={() => setMatchOpen((o) => !o)}
          className={`py-2 px-4 rounded-lg flex items-center gap-2 text-[13px] font-medium border disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${selectedGame ? 'bg-[#2D47B8] text-white border-[#2D47B8]' : 'bg-white text-[#334155] border-[#CBD5E1] hover:border-[#94A3B8]'}`}
        >
          <span className="max-w-[320px] truncate">{matchLabel}</span>
          {isSelectedGameLive && (
            <span className="flex items-center gap-1 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className={`text-[11px] font-semibold ${selectedGame ? 'text-green-300' : 'text-green-500'}`}>Live</span>
            </span>
          )}
          <svg
            className={`w-3 h-3 shrink-0 transition-transform ${matchOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {matchOpen && selectedTeam && (
          <div className="absolute top-[calc(100%+6px)] left-0 w-[300px] bg-white border border-[#CBD5E1] rounded-xl shadow-lg z-50">
            <div className="max-h-[260px] overflow-y-auto py-1">
              {loadingGames ? (
                <div className="px-3 py-3 text-[13px] text-[#94A3B8]">Loading…</div>
              ) : games.length === 0 ? (
                <div className="px-3 py-3 text-[13px] text-[#94A3B8]">No matches found</div>
              ) : (
                games.map((g) => (
                  <button
                    key={g._id}
                    onClick={() => handleGameSelect(g)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#F1F5F9] transition-colors ${
                      selectedGame?._id === g._id ? 'bg-[#EEF2FF]' : ''
                    }`}
                  >
                    <div className="flex flex-col flex-1 min-w-0">
                      <p
                        className={`text-[13px] ${
                          selectedGame?._id === g._id
                            ? 'text-[#3B5BDB] font-medium'
                            : 'text-[#334155]'
                        }`}
                      >
                        {g.home?.name} vs {g.away?.name}
                      </p>
                      <p className="text-[11px] text-[#94A3B8] mt-0.5">
                        {format(new Date(g.date), 'MMM d, yyyy')}
                      </p>
                    </div>
                    {g.status === 'in-play' && (
                      <span className="flex items-center gap-1 shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                        <span className="text-[11px] font-semibold text-green-500">Live</span>
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Aggregate toggle */}
      <label className={`flex items-center gap-2 ml-2 h-9 px-3 rounded-lg border cursor-pointer select-none transition-colors ${isAggregate ? 'border-[#2D47B8] bg-[#EEF2FF]' : 'border-[#CBD5E1] bg-white hover:border-[#94A3B8]'}`}>
        <input
          type="checkbox"
          checked={isAggregate}
          onChange={(e) => onAggregateToggle(e.target.checked)}
          className="w-4 h-4 rounded border-[#CBD5E1] accent-[#2D47B8] cursor-pointer"
        />
        <span className={`text-[13px] ${isAggregate ? 'text-[#3B5BDB] font-medium' : 'text-[#334155]'}`}>View All Teams (Aggregate)</span>
      </label>
    </div>
  );
};

export default FilterBar;
